import { useLocalStorageState, useMemoizedFn } from 'ahooks';
import { isFunction } from 'lodash-es';
import { useMemo } from 'react';
import languageCurrencyMap from 'utils/pay/languageCurrencyMap';
import { useSystemLanguageValue } from '../system/useSystem';
import { useOrgConfigStateValue } from '../useOrgConfig';
import { isQuickChat, isTfsoftware } from 'productLine';

type SetterParams<T> = T | ((prev: T) => T);
type Currency = General.Currency;

const DEFAULT_CURRENCY_ITEMS: General.Currency[] = isQuickChat
  ? ['IDR']
  : ['CNY', 'USD', 'IDR', 'THB', 'VND', 'PHP', 'MYR', 'BRL', 'MXN'];

const useCurrencyState = (
  cacheKey = `@${
    isQuickChat ? 'quickchat' : isTfsoftware ? 'tfsoftware' : 'fett'
  }/default-pay/currency`,
): [Currency, (prev: SetterParams<Currency>) => void] => {
  const lng = useSystemLanguageValue();
  const { airwallexEnable } = useOrgConfigStateValue();
  const [currencyRaw, setCurrency] = useLocalStorageState<Currency>(cacheKey, {
    serializer: (v) => v ?? 'CNY',
    deserializer: (v) => {
      return DEFAULT_CURRENCY_ITEMS.find((t) => t === v) || 'CNY';
    },
  });
  const currency = useMemo(() => {
    if (isQuickChat) {
      return 'IDR';
    } // 固定条件
    if (!airwallexEnable && currencyRaw !== 'CNY') {
      return languageCurrencyMap['zh-CN'];
    }
    return currencyRaw ?? languageCurrencyMap[lng];
  }, [currencyRaw, lng, airwallexEnable]);
  const setter = useMemoizedFn((state: SetterParams<Currency>) => {
    setCurrency((prev) => {
      let next = prev;
      if (isFunction(state)) {
        next = state(prev || 'CNY');
      } else {
        next = state;
      }
      return next;
    });
  });
  return [currency, setter];
};

export const useAvailableCurrencies = () => {
  const { airwallexEnable } = useOrgConfigStateValue();
  return useMemo(
    () => DEFAULT_CURRENCY_ITEMS.filter((t) => (airwallexEnable ? true : t === 'CNY')),
    [airwallexEnable],
  );
};

export default useCurrencyState;
